import React from 'react';
import { TrendingUp, Clock, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения анализа тенденций ликвидности предприятия
 */
const LiquidityTrendAnalysis = ({ currentResults, historicalData }) => {
  if (!currentResults || !historicalData || historicalData.length < 2) return null;

  // Сортируем исторические данные по дате
  const sortedData = [...historicalData].sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  // Получаем последние 5 записей, включая текущую
  const recentData = sortedData.slice(-5);
  
  // Функция для получения тренда показателя
  const getTrend = (currentValue, previousValue) => {
    if (!previousValue) return { direction: 'neutral', icon: <Minus className="w-4 h-4 text-gray-500" />, color: 'text-gray-500' };
    
    const change = currentValue - previousValue;
    const percentChange = (change / previousValue) * 100;
    
    if (Math.abs(percentChange) < 1) {
      return { 
        direction: 'neutral', 
        icon: <Minus className="w-4 h-4 text-gray-500" />, 
        color: 'text-gray-500',
        change,
        percentChange
      };
    } else if (change > 0) {
      return { 
        direction: 'up', 
        icon: <ArrowUpRight className="w-4 h-4 text-green-500" />, 
        color: 'text-green-500',
        change,
        percentChange
      };
    } else {
      return { 
        direction: 'down', 
        icon: <ArrowDownRight className="w-4 h-4 text-red-500" />, 
        color: 'text-red-500',
        change,
        percentChange
      };
    }
  };

  // Получаем данные для текущего и предыдущего периодов
  const currentPeriodData = currentResults;
  const previousPeriodData = sortedData.length > 1 ? sortedData[sortedData.length - 2].results : null;

  // Получаем тренды для основных показателей
  const currentRatioTrend = getTrend(currentPeriodData.currentRatio, previousPeriodData?.currentRatio);
  const quickRatioTrend = getTrend(currentPeriodData.quickRatio, previousPeriodData?.quickRatio);
  const cashRatioTrend = getTrend(currentPeriodData.cashRatio, previousPeriodData?.cashRatio);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-indigo-500" />
        Динамика коэффициентов ликвидности
      </h3>

      <div className="space-y-6">
        {/* Табличное представление трендов */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Показатель
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Текущее значение
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Предыдущее значение
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Изменение
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Тренд
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Коэффициент текущей ликвидности */}
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Коэффициент текущей ликвидности
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
                  {formatNumber(currentPeriodData.currentRatio, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
                  {previousPeriodData ? formatNumber(previousPeriodData.currentRatio, 2) : '—'}
                </td>
                <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${currentRatioTrend.color}`}>
                  {currentRatioTrend.change ? formatNumber(currentRatioTrend.change, 2) : '—'} 
                  {currentRatioTrend.percentChange ? ` (${formatNumber(currentRatioTrend.percentChange, 1)}%)` : ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                  {currentRatioTrend.icon}
                </td>
              </tr>
              
              {/* Коэффициент быстрой ликвидности */}
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Коэффициент быстрой ликвидности
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
                  {formatNumber(currentPeriodData.quickRatio, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
                  {previousPeriodData ? formatNumber(previousPeriodData.quickRatio, 2) : '—'}
                </td>
                <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${quickRatioTrend.color}`}>
                  {quickRatioTrend.change ? formatNumber(quickRatioTrend.change, 2) : '—'} 
                  {quickRatioTrend.percentChange ? ` (${formatNumber(quickRatioTrend.percentChange, 1)}%)` : ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                  {quickRatioTrend.icon}
                </td>
              </tr>
              
              {/* Коэффициент абсолютной ликвидности */}
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Коэффициент абсолютной ликвидности
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
                  {formatNumber(currentPeriodData.cashRatio, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-300">
                  {previousPeriodData ? formatNumber(previousPeriodData.cashRatio, 2) : '—'}
                </td>
                <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${cashRatioTrend.color}`}>
                  {cashRatioTrend.change ? formatNumber(cashRatioTrend.change, 2) : '—'} 
                  {cashRatioTrend.percentChange ? ` (${formatNumber(cashRatioTrend.percentChange, 1)}%)` : ''}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                  {cashRatioTrend.icon}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Визуальное представление трендов */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Текущая ликвидность */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Текущая ликвидность</h4>
              <div className={`flex items-center ${currentRatioTrend.color}`}>
                {currentRatioTrend.icon}
                <span className="ml-1 text-xs">
                  {currentRatioTrend.percentChange ? formatNumber(currentRatioTrend.percentChange, 1) + '%' : ''}
                </span>
              </div>
            </div>
            <div className="flex items-end space-x-1 h-32">
              {recentData.map((item, index) => {
                const value = item.results?.currentRatio || 0;
                const maxHeight = 100; // Максимальная высота в процентах
                const height = Math.min((value / 3) * 100, maxHeight); // Ограничение высоты (3 - это примерный максимум для коэффициента)
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-full rounded-t ${index === recentData.length - 1 ? 'bg-blue-500' : 'bg-blue-300 dark:bg-blue-700'}`} 
                      style={{ height: `${height}%` }}
                    ></div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(item.date).toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Быстрая ликвидность */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Быстрая ликвидность</h4>
              <div className={`flex items-center ${quickRatioTrend.color}`}>
                {quickRatioTrend.icon}
                <span className="ml-1 text-xs">
                  {quickRatioTrend.percentChange ? formatNumber(quickRatioTrend.percentChange, 1) + '%' : ''}
                </span>
              </div>
            </div>
            <div className="flex items-end space-x-1 h-32">
              {recentData.map((item, index) => {
                const value = item.results?.quickRatio || 0;
                const maxHeight = 100; // Максимальная высота в процентах
                const height = Math.min((value / 2) * 100, maxHeight); // Ограничение высоты (2 - это примерный максимум для коэффициента)
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-full rounded-t ${index === recentData.length - 1 ? 'bg-purple-500' : 'bg-purple-300 dark:bg-purple-700'}`} 
                      style={{ height: `${height}%` }}
                    ></div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(item.date).toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Абсолютная ликвидность */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Абсолютная ликвидность</h4>
              <div className={`flex items-center ${cashRatioTrend.color}`}>
                {cashRatioTrend.icon}
                <span className="ml-1 text-xs">
                  {cashRatioTrend.percentChange ? formatNumber(cashRatioTrend.percentChange, 1) + '%' : ''}
                </span>
              </div>
            </div>
            <div className="flex items-end space-x-1 h-32">
              {recentData.map((item, index) => {
                const value = item.results?.cashRatio || 0;
                const maxHeight = 100; // Максимальная высота в процентах
                const height = Math.min((value / 1) * 100, maxHeight); // Ограничение высоты (1 - это примерный максимум для коэффициента)
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-full rounded-t ${index === recentData.length - 1 ? 'bg-emerald-500' : 'bg-emerald-300 dark:bg-emerald-700'}`} 
                      style={{ height: `${height}%` }}
                    ></div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(item.date).toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Пояснение */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start">
          <Clock className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Интерпретация динамики
            </h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Анализ динамики показателей ликвидности позволяет оценить тенденции изменения платежеспособности предприятия. Положительная динамика свидетельствует об улучшении финансового состояния, отрицательная - о возможных проблемах с платежеспособностью. Для комплексной оценки необходимо рассматривать динамику всех показателей в совокупности.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiquidityTrendAnalysis;